export interface PersonalDetails {
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  dateOfBirth: string; // ISO date string
  gender: 'Male' | 'Female' | 'Other' | 'Prefer not to say';
  nationality: string;
  maritalStatus: 'Single' | 'Married' | 'Divorced' | 'Widowed' | 'Civil Partnership' | 'Other';

  // Contact Information
  email: string;
  personalEmail?: string;
  mobilePhone: string;
  homePhone?: string;

  // Address Information
  address: Address;

  // UK Specific
  nationalInsuranceNumber?: string; // NI Number
  passportNumber?: string;
  visaStatus?: 'British Citizen' | 'EU Settled Status' | 'EU Pre-Settled Status' | 'Work Visa' | 'Student Visa' | 'Other';
  rightToWork: boolean;



  // Additional
  profileImage?: string;
  notes?: string;
}

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
}

export interface EmergencyContact {
  name: string;
  relationship: string;
  mobilePhone: string;
  homePhone?: string;
  email?: string;
  address?: Address;
}

export interface JobDetails {
  // Basic Job Information
  jobTitle: string;
  department: string;
  team?: string;
  location: string;

  // Employment Details
  employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Temporary' | 'Apprentice' | 'Intern';
  contractType: 'Permanent' | 'Fixed-term' | 'Zero-hours' | 'Casual';
  startDate: string; // ISO date string
  endDate?: string; // For fixed-term contracts
  probationEndDate?: string;

  // Reporting Structure
  managerId?: string;
  managerName?: string;
  reportsTo?: string; // For display purposes

  // Work Schedule
  workingHours: number; // Hours per week
  workingPattern: 'Standard' | 'Flexible' | 'Shift Work' | 'Remote' | 'Hybrid';
  workingDays: string[]; // ['Monday', 'Tuesday', etc.]

  // Compensation
  salary?: number;
  salaryFrequency?: 'Annual' | 'Monthly' | 'Weekly' | 'Hourly';
  currency: string;

  // Status and Classification
  employeeStatus: 'Active' | 'On Leave' | 'Suspended' | 'Terminated' | 'Retired';
  employeeNumber?: string;
  costCenter?: string;

  // UK Specific
  pensionScheme?: string;
  taxCode?: string;

  // Additional
  jobDescription?: string;
  notes?: string;
}

export interface PayeeDetails {
  // Bank Account Information
  bankName?: string;
  accountHolderName?: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
  swiftCode?: string;

  // Payment Preferences
  paymentMethod?: string; // Bank Transfer, Cheque, Cash, etc.
  paymentFrequency?: string; // Weekly, Monthly, etc.
  paymentReference?: string;

  // Tax Information
  taxCode?: string;
  p45Reference?: string;
  p60Reference?: string;
  studentLoanDeduction?: boolean;
  studentLoanPlan?: string; // Plan 1, Plan 2, Postgraduate, etc.

  // Pension Information
  pensionOptOut?: boolean;
  pensionProvider?: string;
  pensionReference?: string;
  employeeContributionPercentage?: number;
  employerContributionPercentage?: number;

  // Additional Deductions
  unionFees?: number;
  unionName?: string;
  otherDeductions?: number;
  otherDeductionsDescription?: string;

  // Benefits
  carAllowance?: number;
  travelAllowance?: number;
  mealAllowance?: number;
  benefitsPackage?: string;

  // Additional
  notes?: string;
}

export interface Employee {
  id: string;
  personalDetails: PersonalDetails;
  jobDetails: JobDetails;
  payeeDetails?: PayeeDetails;
  emergencyContact?: EmergencyContact;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface EmployeeListItem {
  id: string;
  fullName: string;
  profileImage?: string;
  role: string; // From jobDetails.jobTitle
  department: string; // From jobDetails.department
  status: string; // From jobDetails.employeeStatus
  email?: string;
  employeeNumber?: string;
  managerId?: string;
  managerName?: string;
}

export interface EmployeeSearchParams {
  searchTerm?: string;
  sortBy?: 'name' | 'role' | 'department' | 'status';
  sortOrder?: 'asc' | 'desc';
  department?: string;
  status?: string;
  employmentType?: string;
  managerId?: string;
}

export interface EmployeeActionItem {
  id: string;
  label: string;
  icon: string;
  action: string;
  disabled?: boolean;
}

// Form interfaces for multi-step employee creation
export interface PersonalDetailsForm {
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  maritalStatus: string;
  email: string;
  personalEmail?: string;
  mobilePhone: string;
  homePhone?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
  nationalInsuranceNumber?: string;
  passportNumber?: string;
  visaStatus?: string;
  rightToWork: boolean | string;

  profileImage?: string;
  notes?: string;
}

export interface JobDetailsForm {
  jobTitle: string;
  department: string;
  team?: string;
  location: string;
  employmentType: string;
  contractType: string;
  startDate: string;
  endDate?: string;
  probationEndDate?: string;
  managerId?: string;
  workingHours: number;
  workingPattern: string;
  workingDays: string[];
  salary?: number;
  salaryFrequency?: string;
  currency: string;
  employeeStatus: string;
  employeeNumber?: string;
  costCenter?: string;
  pensionScheme?: string;
  taxCode?: string;
  jobDescription?: string;
  notes?: string;
}

export interface PayeeDetailsForm {
  // Bank Account Information
  bankName?: string;
  accountHolderName?: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
  swiftCode?: string;

  // Payment Preferences
  paymentMethod?: string;
  paymentFrequency?: string;
  paymentReference?: string;

  // Tax Information
  taxCode?: string;
  p45Reference?: string;
  p60Reference?: string;
  studentLoanDeduction?: boolean;
  studentLoanPlan?: string;

  // Pension Information
  pensionOptOut?: boolean;
  pensionProvider?: string;
  pensionReference?: string;
  employeeContributionPercentage?: number;
  employerContributionPercentage?: number;

  // Additional Deductions
  unionFees?: number;
  unionName?: string;
  otherDeductions?: number;
  otherDeductionsDescription?: string;

  // Benefits
  carAllowance?: number;
  travelAllowance?: number;
  mealAllowance?: number;
  benefitsPackage?: string;

  // Additional
  notes?: string;
}

export interface EmergencyDetailsForm {
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactMobile: string;
  emergencyContactHome?: string;
  emergencyContactEmail?: string;
  emergencyContactAddressLine1?: string;
  emergencyContactAddressLine2?: string;
  emergencyContactCity?: string;
  emergencyContactCounty?: string;
  emergencyContactPostcode?: string;
  emergencyContactCountry?: string;
}

export interface CreateEmployeeRequest {
  personalDetails: PersonalDetailsForm;
  jobDetails: JobDetailsForm;
  payeeDetails?: PayeeDetailsForm;
  emergencyDetails: EmergencyDetailsForm;
}

export interface UpdateEmployeeRequest {
  personalDetails: PersonalDetailsForm;
  jobDetails: JobDetailsForm;
  payeeDetails?: PayeeDetailsForm;
  emergencyDetails: EmergencyDetailsForm;
}

// Manager selection interface
export interface ManagerOption {
  id: string;
  name: string;
  jobTitle: string;
  department: string;
  profileImage?: string;
}
